import { offerBillingLabel } from '../../lib/offerBilling';
import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Eye,
  Globe,
  Info,
  Layers,
  MapPin,
  Package,
  Pencil,
  Search,
  ShieldCheck,
  Trash2,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useScope } from '@workspace/scope-context';
import {
  Callout,
  Checkbox,
  CommerceCatalogWorkspace,
  CommerceCombobox,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  DescriptionText,
  Field,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  RegistryEmptyState,
  RegistryStatus,
  SectionHeading,
  Surface,
  TechnicalValue,
  VerticalStack,
  WizardWorkspace,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
  type WizardTab,
  toast,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const STOREFRONT_SORT_FIELDS = ['eligibility_mode', 'experience_key', 'name', 'segment', 'status', 'storefront', 'updated_at'] as const;
import { cn } from '@/lib/utils';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
  formatRelativeTime,
} from '@/views/commerce/catalogHierarchyShared';
import {
  createStorefrontExperience,
  deleteStorefrontExperience,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listAccessPointLayers,
  listCatalogs,
  listCommerceChannels,
  listCommerceChannelPublications,
  listPricebooks,
  listPublications,
  listStorefrontExperiences,
  previewEligibility,
  repairStorefrontExperience,
  replaceCommerceChannelPublications,
  updateStorefrontExperience,
  validateStorefrontExperience,
  type AdminEligibilityPreviewDiagnostic,
  type EligibilityPolicy,
  type AdminEligibilityPreviewProduct,
  type AdminEligibilityPreviewProductBlock,
  type AdminEligibilityPreviewResponse,
  type AdminEligibilityPreviewSection,
  type Catalog,
  type CatalogPublication,
  type CommerceChannel,
  type CommerceChannelPublication,
  type Market,
  type MarketGroup,
  type Pricebook,
  type Product,
  type StorefrontExperience,
} from '@/lib/commerce';
import type { CommerceSelectOptionViewModel } from '@/features/commerce-admin/headless-contract';
import {
  buildChannelSelectorOptions,
  buildMarketSelectorOptions,
  buildObservedScopeOptions,
  buildProductSelectorOptions,
  buildPublicationSelectorOptions,
  buildMarketGroupSelectorOptions,
  buildServiceClassSelectorOptions,
  buildServiceGroupSelectorOptions,
  buildTenantSelectorOptions,
  buildVariantSelectorOptions,
  loadCommerceSelectorRecords,
} from './selectorOptions';
import { CommerceEligibilityAddressInput } from './CommerceEligibilityAddressInput';
import {
  StorefrontsWizardEditor,
  type StorefrontEditorMode,
  type StorefrontFormState,
  type StorefrontPrincipalRow,
  type StorefrontPublicationBindingRow,
} from './StorefrontsWizardEditor';
import { EligibilityRulesPanel, EligibilityRuleEditorWizard } from './EligibilityRulesWizard';

type DiagnosticsIndex = Map<string | null | undefined, AdminEligibilityPreviewDiagnostic[]>;

type StorefrontPreviewFormState = {
  address: string;
  googlePlaceId: string;
  lat: number | null;
  lng: number | null;
  placeData: Record<string, unknown> | null;
  eligibilityMode: string;
  routeProfile: string;
  buildType: string;
  sellingTenantId: string;
  storeId: string;
  agentUserId: string;
  includeDrafts: boolean;
};

function createRowId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function channelPublicationToBindingRow(item: CommerceChannelPublication): StorefrontPublicationBindingRow {
  return {
    id: createRowId('publication-route'),
    publicationId: item.publication_id,
    bindingRole: item.binding_role === 'secondary' ? 'secondary' : 'primary',
    status: item.status === 'inactive' ? 'inactive' : 'active',
    priority: item.priority || 0,
    audience: item.audience || 'any',
    targetType: item.market_id ? 'market' : item.market_group_id ? 'market_group' : 'all',
    targetId: item.market_id || item.market_group_id || '',
  };
}

function createEmptyForm(): StorefrontFormState {
  return {
    name: '',
    experienceKey: '',
    segment: 'residential',
    eligibilityMode: 'zone_only',
    routeProfile: 'fiber_construction',
    runtimeFamily: 'public_signup',
    status: 'ready',
    storefrontKey: '',
    channelId: '',
    confirmationRedirectUrl: '',
    autoCreateChannel: true,
  };
}

function createPreviewForm(item: StorefrontExperience | null): StorefrontPreviewFormState {
  return {
    address: '',
    googlePlaceId: '',
    lat: null,
    lng: null,
    placeData: null,
    eligibilityMode: item?.eligibility_mode || (item?.segment === 'business' ? 'zone_and_road_distance' : 'zone_only'),
    routeProfile: item?.route_profile || 'fiber_construction',
    buildType: '',
    sellingTenantId: '',
    storeId: '',
    agentUserId: '',
    includeDrafts: false,
  };
}

function buildOptionLookup(options: CommerceSelectOptionViewModel[]): Map<string, CommerceSelectOptionViewModel> {
  return new Map(options.map(option => [option.value, option]));
}

function formatPriceCents(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null) {
    return '';
  }
  const symbol = (currency || 'USD').toUpperCase() === 'USD' ? '$' : `${currency} `;
  return `${symbol}${(amount / 100).toFixed(2)}`;
}

function formatCurrencyAmount(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null) {
    return 'Not available';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDistanceMeters(distance: number | null | undefined): string {
  if (distance == null) {
    return 'Not available';
  }
  return `${Math.round(distance).toLocaleString()} m`;
}

function formatRouteProfile(profile: string): string {
  return profile === 'fiber_construction' ? 'Fiber Construction' : previewStatusLabel(profile);
}

function formatRouteEndpointLabel(endpoint?: { id?: string | null; name?: string | null; latitude: number; longitude: number } | null): string {
  if (!endpoint) {
    return 'Not available';
  }
  if (endpoint.name?.trim()) {
    return endpoint.name.trim();
  }
  if (endpoint.id?.trim()) {
    return endpoint.id.trim();
  }
  return `${endpoint.latitude.toFixed(6)}, ${endpoint.longitude.toFixed(6)}`;
}

function previewStatusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function previewStatusVariant(status: string): 'success' | 'warning' | 'error' {
  switch (status) {
    case 'serviceable':
      return 'success';
    case 'quote_required':
      return 'warning';
    default:
      return 'error';
  }
}

function previewProductLabel(
  product: AdminEligibilityPreviewResponse['sellable_products'][number] | AdminEligibilityPreviewResponse['blocked_products'][number],
): string {
  return product.display_name?.trim() || product.name?.trim() || product.product_id;
}

function previewProductDescription(
  product: AdminEligibilityPreviewResponse['sellable_products'][number] | AdminEligibilityPreviewResponse['blocked_products'][number],
): string {
  return product.description?.trim() || 'No description provided.';
}

function toOptionalString(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function readinessVariant(value: string | undefined): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((value || '').toLowerCase()) {
    case 'ready':
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'broken':
      return 'error';
    case 'archived':
      return 'muted';
    default:
      return 'default';
  }
}

function principalRowsFromExperience(item?: StorefrontExperience | null): StorefrontPrincipalRow[] {
  if (!item?.principal_defaults?.length) {
    return [{ id: createRowId('principal'), principalType: 'oidc_client', principalId: 'bff-acd' }];
  }
  return item.principal_defaults.map(principal => ({
    id: createRowId('principal'),
    principalType: principal.principal_type,
    principalId: principal.principal_id,
  }));
}

function validateForm(form: StorefrontFormState, principalRows: StorefrontPrincipalRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) {
    errors.name = 'Name is required.';
  }
  if (!form.experienceKey.trim()) {
    errors.experienceKey = 'Experience key is required.';
  }
  if (!form.storefrontKey.trim() && !form.experienceKey.trim()) {
    errors.storefrontKey = 'Storefront key is required.';
  }
  if (form.confirmationRedirectUrl.trim()) {
    try {
      const redirect = new URL(form.confirmationRedirectUrl.trim());
      if (redirect.protocol !== 'https:' || !redirect.hostname || redirect.username || redirect.password) {
        errors.confirmationRedirectUrl = 'Enter an absolute HTTPS URL without credentials.';
      }
    } catch {
      errors.confirmationRedirectUrl = 'Enter an absolute HTTPS URL.';
    }
  }
  for (const principal of principalRows) {
    const hasType = principal.principalType.trim().length > 0;
    const hasId = principal.principalId.trim().length > 0;
    if ((hasType && !hasId) || (!hasType && hasId)) {
      errors.principals = 'Each principal row needs both a type and an id.';
      break;
    }
  }
  return errors;
}

function formatSnippet(item: StorefrontExperience | null): string {
  if (!item) {
    return `{
  "commerce": {
    "publicExperienceKey": "acd-residential-signup"
  }
}`;
  }
  if (item.segment === 'business') {
    return `{
  "commerce": {
    "businessExperienceKey": "${item.experience_key}"
  }
}`;
  }
  return `{
  "commerce": {
    "publicExperienceKey": "${item.experience_key}"
  }
}`;
}

function formatExperienceStatus(item: StorefrontExperience): string {
  return item.readiness?.effective_status || item.status || 'unknown';
}

function firstRepairAction(item: StorefrontExperience | null): string | null {
  const action = item?.readiness?.issues.find(issue => issue.repair_action?.trim())?.repair_action?.trim();
  return action || null;
}

function SeverityBadge({ severity }: { severity: string }) {
  const normalized = (severity || '').toLowerCase();
  if (normalized === 'error') {
    return (
      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-rose-500/10 text-rose-700 ring-rose-500/20">
        <AlertCircle className="h-2.5 w-2.5" />Error
      </span>
    );
  }
  if (normalized === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-amber-500/10 text-amber-700 ring-amber-500/20">
        <AlertTriangle className="h-2.5 w-2.5" />Warning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-blue-500/10 text-blue-700 ring-blue-500/20">
      <Info className="h-2.5 w-2.5" />{severity || 'Info'}
    </span>
  );
}

function DiagnosticCard({ diagnostic }: { diagnostic: AdminEligibilityPreviewDiagnostic }) {
  const [metadataOpen, setMetadataOpen] = useState(false);
  const hasMetadata = Boolean(diagnostic.metadata && Object.keys(diagnostic.metadata).length > 0);
  const severity = (diagnostic.severity || '').toLowerCase();
  const borderColor = severity === 'error'
    ? 'border-rose-200/60'
    : severity === 'warning'
      ? 'border-amber-200/60'
      : 'border-border/40';

  return (
    <div className={cn('rounded-lg border bg-card/60 p-3.5', borderColor)}>
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={diagnostic.severity} />
        <span className="rounded bg-muted/80 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
          {diagnostic.code}
        </span>
        {diagnostic.kind && diagnostic.kind !== diagnostic.code ? (
          <span className="rounded border border-border/40 px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground/70">
            {diagnostic.kind}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-xs font-medium leading-relaxed text-foreground">{diagnostic.message}</p>

      {(diagnostic.product_id || diagnostic.variant_id || diagnostic.policy_id) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {diagnostic.product_id ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
              <Package className="h-3 w-3 shrink-0" />
              Product <TechnicalValue className="text-[9px]">{diagnostic.product_id}</TechnicalValue>
            </span>
          ) : null}
          {diagnostic.variant_id ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
              <Layers className="h-3 w-3 shrink-0" />
              Variant <TechnicalValue className="text-[9px]">{diagnostic.variant_id}</TechnicalValue>
            </span>
          ) : null}
          {diagnostic.policy_id ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
              <ShieldCheck className="h-3 w-3 shrink-0" />
              Policy <TechnicalValue className="text-[9px]">{diagnostic.policy_id}</TechnicalValue>
            </span>
          ) : null}
        </div>
      )}

      {diagnostic.missing_fields && diagnostic.missing_fields.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Missing:</span>
          {diagnostic.missing_fields.map(field => (
            <TechnicalValue key={field} className="text-[10px] text-rose-600/80">
              {field}
            </TechnicalValue>
          ))}
        </div>
      ) : null}

      {hasMetadata ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setMetadataOpen(current => !current)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground/70 transition-colors hover:text-foreground"
          >
            {metadataOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Metadata
          </button>
          {metadataOpen ? (
            <pre className="mt-1.5 max-h-40 overflow-auto rounded bg-muted/40 p-2.5 text-[10px] font-mono leading-relaxed text-muted-foreground">
              {JSON.stringify(diagnostic.metadata, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SellableVariantRow({ variant }: { variant: any }) {
  const label = variant.display_name || variant.offer_name || variant.variant_key || variant.variant_id || '';
  const price = variant.price != null ? formatPriceCents(variant.price, variant.currency) : null;
  const discounted = variant.discounted_price != null && variant.discounted_price !== variant.price
    ? formatPriceCents(variant.discounted_price, variant.currency)
    : null;
  const startup = variant.startup_fee != null && variant.startup_fee > 0
    ? formatPriceCents(variant.startup_fee, variant.currency)
    : null;

  return (
    <div className="flex items-start justify-between gap-3 bg-muted/10 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-black leading-tight text-foreground">{label}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          {variant.offer_type ? <DescriptionText className="text-[10px]">{variant.offer_type}</DescriptionText> : null}
          {variant.charge_type && variant.charge_type !== variant.offer_type ? (
            <DescriptionText className="text-[10px]">{variant.charge_type}</DescriptionText>
          ) : null}
          {variant.billing_period_months ? <DescriptionText className="text-[10px]">/{offerBillingLabel(variant.billing_period_months)}</DescriptionText> : null}
        </div>
        {variant.offer_key ? (
          <TechnicalValue className="mt-0.5 text-[9px] text-muted-foreground/50">{variant.offer_key}</TechnicalValue>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        {price ? (
          <p className={cn('text-xs font-black', discounted ? 'text-muted-foreground line-through' : 'text-foreground')}>
            {price}
          </p>
        ) : null}
        {discounted ? <p className="text-xs font-black text-emerald-700">{discounted}</p> : null}
        {startup ? <p className="text-[10px] font-bold text-muted-foreground">+{startup} setup</p> : null}
      </div>
    </div>
  );
}

function SellableProductCard({ product }: { product: AdminEligibilityPreviewProduct }) {
  const [variantsOpen, setVariantsOpen] = useState(false);
  const variantCount = product.variants.length;

  return (
    <div className="overflow-hidden rounded-lg border border-emerald-200/60 bg-card shadow-sm">
      <div className="flex items-start gap-3 p-3">
        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black leading-tight text-foreground">{previewProductLabel(product)}</p>
          {previewProductDescription(product) ? (
            <DescriptionText className="mt-0.5 line-clamp-2 text-[11px]">{previewProductDescription(product)}</DescriptionText>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-1">
            {!product.visible ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-amber-500/10 text-amber-700 ring-amber-500/20">
                Hidden
              </span>
            ) : null}
            {product.agents_only ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-purple-500/10 text-purple-700 ring-purple-500/20">
                Agents Only
              </span>
            ) : null}
            {product.requires_bundle ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-blue-500/10 text-blue-700 ring-blue-500/20">
                Bundle Req
              </span>
            ) : null}
            {product.limit_per_cart > 0 && product.limit_per_cart < 100 ? (
              <span className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-muted text-muted-foreground ring-border/40">
                Limit {product.limit_per_cart}
              </span>
            ) : null}
            {product.class ? (
              <span className="rounded border border-border/30 px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground/70">
                {product.class}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      {variantCount > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setVariantsOpen(current => !current)}
            className="flex w-full items-center justify-between border-t border-border/40 bg-muted/5 px-3 py-2 text-left transition-colors hover:bg-muted/15"
          >
            <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700">
              <Layers className="h-3 w-3" />
              {variantCount} Eligible Variant{variantCount === 1 ? '' : 's'}
            </span>
            {variantsOpen ? <ChevronDown className="h-3.5 w-3.5 text-emerald-600" /> : <ChevronRight className="h-3.5 w-3.5 text-emerald-600" />}
          </button>
          {variantsOpen ? (
            <div className="divide-y divide-border/30 border-t border-border/40">
              {product.variants.map((variant, index) => (
                <SellableVariantRow
                  key={(variant as any).variant_id ?? String(index)}
                  variant={variant}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function BlockedProductCard({
  product,
  linkedDiagnostics,
  defaultDetailsOpen = false,
}: {
  product: AdminEligibilityPreviewProductBlock;
  linkedDiagnostics: AdminEligibilityPreviewDiagnostic[];
  defaultDetailsOpen?: boolean;
}) {
  const [detailsOpen, setDetailsOpen] = useState(defaultDetailsOpen);
  const blockedVariantCount = product.blocked_variants.length;
  const diagnosticsCount = linkedDiagnostics.length;
  const hasDetails = blockedVariantCount > 0 || diagnosticsCount > 0;

  return (
    <div className="overflow-hidden rounded-lg border border-rose-200/60 bg-card shadow-sm">
      <div className="flex items-start gap-3 p-3">
        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-rose-500/10">
          <XCircle className="h-3.5 w-3.5 text-rose-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black leading-tight text-foreground">{previewProductLabel(product)}</p>
          <DescriptionText className="mt-0.5 line-clamp-2 text-[11px]">{previewProductDescription(product)}</DescriptionText>
          {product.reasons.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {product.reasons.map(reason => (
                <span
                  key={reason}
                  className="rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-rose-500/10 text-rose-700 ring-rose-500/20"
                >
                  {reason.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          ) : null}
          {product.policy_ids && product.policy_ids.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Policies:</span>
              {product.policy_ids.map(policyId => (
                <TechnicalValue key={policyId} className="text-[10px]">
                  {policyId}
                </TechnicalValue>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {hasDetails ? (
        <>
          <button
            type="button"
            onClick={() => setDetailsOpen(current => !current)}
            className="flex w-full items-center justify-between border-t border-border/40 bg-muted/5 px-3 py-2 text-left transition-colors hover:bg-muted/15"
          >
            <span className="flex items-center gap-3">
              {blockedVariantCount > 0 ? (
                <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-rose-700">
                  <Layers className="h-3 w-3" />
                  {blockedVariantCount} Variant{blockedVariantCount === 1 ? '' : 's'}
                </span>
              ) : null}
              {diagnosticsCount > 0 ? (
                <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-700">
                  <AlertTriangle className="h-3 w-3" />
                  {diagnosticsCount} Diagnostic{diagnosticsCount === 1 ? '' : 's'}
                </span>
              ) : null}
            </span>
            {detailsOpen ? <ChevronDown className="h-3.5 w-3.5 text-rose-600" /> : <ChevronRight className="h-3.5 w-3.5 text-rose-600" />}
          </button>

          {detailsOpen ? (
            <div className="border-t border-border/40">
              {blockedVariantCount > 0 ? (
                <div className="divide-y divide-border/30">
                  {product.blocked_variants.map(variant => (
                    <div key={variant.variant_id} className="bg-muted/10 px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-rose-500/60" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-black leading-tight text-foreground">
                            {variant.display_name || variant.variant_key || variant.variant_id}
                          </p>
                          {variant.reasons.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {variant.reasons.map(reason => (
                                <span
                                  key={reason}
                                  className="rounded px-1 py-0.5 text-[9px] font-black uppercase tracking-widest ring-1 bg-rose-500/10 text-rose-700 ring-rose-500/20"
                                >
                                  {reason.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {variant.policy_ids && variant.policy_ids.length > 0 ? (
                            <div className="mt-1 flex flex-wrap items-center gap-1">
                              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60">Policies:</span>
                              {variant.policy_ids.map(policyId => (
                                <TechnicalValue key={policyId} className="text-[9px]">
                                  {policyId}
                                </TechnicalValue>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              {diagnosticsCount > 0 ? (
                <div className={cn('px-3 py-3', blockedVariantCount > 0 ? 'border-t border-amber-200/40' : undefined)}>
                  <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                    Linked Diagnostics
                  </p>
                  <VerticalStack gap={2}>
                    {linkedDiagnostics.map((diagnostic, index) => (
                      <DiagnosticCard key={`${diagnostic.code}-${index}`} diagnostic={diagnostic} />
                    ))}
                  </VerticalStack>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function PreviewSectionPanel({
  section,
  sellableById,
  blockedById,
  diagnosticsByProductId,
}: {
  section: AdminEligibilityPreviewSection;
  sellableById: Map<string, AdminEligibilityPreviewProduct>;
  blockedById: Map<string, AdminEligibilityPreviewProductBlock>;
  diagnosticsByProductId: DiagnosticsIndex;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const sellableCount = section.sellable_product_ids.length;
  const blockedCount = section.blocked_product_ids.length;
  const totalCount = sellableCount + blockedCount;

  return (
    <Surface className="overflow-hidden border-border/50 p-0 shadow-sm">
      <button
        type="button"
        onClick={() => setCollapsed(current => !current)}
        className="flex w-full items-start justify-between px-4 py-3 text-left transition-colors hover:bg-muted/5"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-black text-foreground">{section.name}</p>
            <TechnicalValue className="text-[9px] text-muted-foreground/50">{section.section_key}</TechnicalValue>
          </div>
          {section.description ? <DescriptionText className="mt-0.5 text-[11px]">{section.description}</DescriptionText> : null}
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          {sellableCount > 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-emerald-500/10 text-emerald-700 ring-emerald-500/20">
              {sellableCount} sellable
            </span>
          ) : null}
          {blockedCount > 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-rose-500/10 text-rose-700 ring-rose-500/20">
              {blockedCount} blocked
            </span>
          ) : null}
          {totalCount === 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-muted text-muted-foreground ring-border/40">
              empty
            </span>
          ) : null}
          {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground/60" /> : <ChevronDown className="h-4 w-4 text-muted-foreground/60" />}
        </div>
      </button>

      {!collapsed ? (
        <div className="border-t border-border/40 p-4">
          {totalCount === 0 ? (
            <DescriptionText className="text-xs">No products in this section for the current parameters.</DescriptionText>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <VerticalStack gap={3}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                    Sellable ({sellableCount})
                  </p>
                </div>
                {sellableCount === 0 ? (
                  <DescriptionText className="rounded border border-border/30 bg-muted/5 px-3 py-2 text-xs">
                    No sellable products in this section.
                  </DescriptionText>
                ) : (
                  section.sellable_product_ids.map(productId => {
                    const product = sellableById.get(productId);
                    if (!product) {
                      return null;
                    }
                    return <SellableProductCard key={product.product_id} product={product} />;
                  })
                )}
              </VerticalStack>

              <VerticalStack gap={3}>
                <div className="flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5 text-rose-600" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">
                    Blocked ({blockedCount})
                  </p>
                </div>
                {blockedCount === 0 ? (
                  <DescriptionText className="rounded border border-border/30 bg-muted/5 px-3 py-2 text-xs">
                    No blocked products in this section.
                  </DescriptionText>
                ) : (
                  section.blocked_product_ids.map(productId => {
                    const product = blockedById.get(productId);
                    if (!product) {
                      return null;
                    }
                    return (
                      <BlockedProductCard
                        key={product.product_id}
                        product={product}
                        linkedDiagnostics={diagnosticsByProductId.get(product.product_id) || []}
                      />
                    );
                  })
                )}
              </VerticalStack>
            </div>
          )}
        </div>
      ) : null}
    </Surface>
  );
}

function PreviewUngroupedResultsPanel({
  sellableProducts,
  blockedProducts,
  diagnosticsByProductId,
}: {
  sellableProducts: AdminEligibilityPreviewProduct[];
  blockedProducts: AdminEligibilityPreviewProductBlock[];
  diagnosticsByProductId: DiagnosticsIndex;
}) {
  const sellableCount = sellableProducts.length;
  const blockedCount = blockedProducts.length;
  const totalCount = sellableCount + blockedCount;

  return (
    <Surface className="overflow-hidden border-border/50 p-0 shadow-sm">
      <div className="flex w-full items-start justify-between px-4 py-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-black text-foreground">Resolved Products</p>
            <TechnicalValue className="text-[9px] text-muted-foreground/50">ungrouped</TechnicalValue>
          </div>
          <DescriptionText className="mt-0.5 text-[11px]">
            This preview returned products without publication section grouping. Expand any blocked product card to inspect exact product, variant, and diagnostic reasons.
          </DescriptionText>
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          {sellableCount > 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-emerald-500/10 text-emerald-700 ring-emerald-500/20">
              {sellableCount} sellable
            </span>
          ) : null}
          {blockedCount > 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-rose-500/10 text-rose-700 ring-rose-500/20">
              {blockedCount} blocked
            </span>
          ) : null}
          {totalCount === 0 ? (
            <span className="rounded px-2 py-0.5 text-[10px] font-black ring-1 bg-muted text-muted-foreground ring-border/40">
              empty
            </span>
          ) : null}
        </div>
      </div>

      <div className="border-t border-border/40 p-4">
        {totalCount === 0 ? (
          <DescriptionText className="text-xs">No sellable or blocked products were returned for this preview.</DescriptionText>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <VerticalStack gap={3}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                  Sellable ({sellableCount})
                </p>
              </div>
              {sellableCount === 0 ? (
                <DescriptionText className="rounded border border-border/30 bg-muted/5 px-3 py-2 text-xs">
                  No sellable products were returned.
                </DescriptionText>
              ) : (
                sellableProducts.map(product => (
                  <SellableProductCard key={product.product_id} product={product} />
                ))
              )}
            </VerticalStack>

            <VerticalStack gap={3}>
              <div className="flex items-center gap-2">
                <XCircle className="h-3.5 w-3.5 text-rose-600" />
                <p className="text-[10px] font-black uppercase tracking-widest text-rose-700">
                  Blocked ({blockedCount})
                </p>
              </div>
              {blockedCount === 0 ? (
                <DescriptionText className="rounded border border-border/30 bg-muted/5 px-3 py-2 text-xs">
                  No blocked products were returned.
                </DescriptionText>
              ) : (
                blockedProducts.map(product => (
                  <BlockedProductCard
                    key={product.product_id}
                    product={product}
                    linkedDiagnostics={diagnosticsByProductId.get(product.product_id) || []}
                    defaultDetailsOpen
                  />
                ))
              )}
            </VerticalStack>
          </div>
        )}
      </div>
    </Surface>
  );
}

function EligibilityOutcomePanel({
  result,
}: {
  result: AdminEligibilityPreviewResponse;
}) {
  const marketResolved = Boolean(result.resolved_market?.name);
  const zoneResolved = Boolean(result.resolved_zone?.name);
  const statusVariant = previewStatusVariant(result.status || 'no_coverage');
  const signal = result.route_distance_signal;
  const estimate = result.preliminary_estimate;
  const showRouting = Boolean(signal || estimate);
  const statusClasses = statusVariant === 'success'
    ? 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/20'
    : statusVariant === 'warning'
      ? 'bg-amber-500/10 text-amber-700 ring-amber-500/20'
      : 'bg-rose-500/10 text-rose-700 ring-rose-500/20';
  const statusIcon = statusVariant === 'success'
    ? <CheckCircle2 className="h-3 w-3" />
    : statusVariant === 'warning'
      ? <AlertTriangle className="h-3 w-3" />
      : <XCircle className="h-3 w-3" />;

  return (
    <Surface className="border-border/50 p-5 shadow-sm">
      <VerticalStack gap={5}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1',
            statusClasses,
          )}>
            {statusIcon}
            {previewStatusLabel(result.status || 'no_coverage')}
          </span>

          <span className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1',
            result.quote_required
              ? 'bg-amber-500/10 text-amber-700 ring-amber-500/20'
              : 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/20',
          )}>
            <Activity className="h-3 w-3" />
            {result.quote_required ? 'Quote Required' : 'Direct Checkout'}
          </span>

          <span className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1',
            marketResolved ? 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/20' : 'bg-rose-500/10 text-rose-700 ring-rose-500/20',
          )}>
            <MapPin className="h-3 w-3" />
            {result.resolved_market?.name || 'No Market'}
          </span>

          {zoneResolved ? (
            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 bg-blue-500/10 text-blue-700 ring-blue-500/20">
              <Globe className="h-3 w-3" />
              {result.resolved_zone?.name}
            </span>
          ) : null}
        </div>

        {result.customer_message ? (
          <Callout variant={result.quote_required ? 'warning' : 'error'}>
            {result.customer_message}
          </Callout>
        ) : null}

        <div>
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Address &amp; Serviceability
          </p>
          <PropertyGrid cols={2}>
            <PropertyItem
              label="Address"
              value={result.resolved_address.address || result.input_summary.address || 'Not resolved'}
              mono={false}
            />
            {result.resolved_address.lat != null && result.resolved_address.lng != null ? (
              <PropertyItem
                label="Coordinates"
                value={`${result.resolved_address.lat.toFixed(6)}, ${result.resolved_address.lng.toFixed(6)}`}
                mono={false}
              />
            ) : null}
            <PropertyItem
              label="Zone"
              value={result.resolved_zone ? `${result.resolved_zone.name} (${result.resolved_zone.zone_kind})` : 'Not resolved'}
              mono={false}
            />
            {result.resolved_zone?.source_ref ? (
              <PropertyItem label="Zone Source" value={result.resolved_zone.source_ref} mono={false} />
            ) : null}
            <PropertyItem
              label="Eligibility Type"
              value={previewStatusLabel(result.resolved_eligibility_mode)}
              mono={false}
            />
            {result.resolved_route_profile ? (
              <PropertyItem label="Route Profile" value={formatRouteProfile(result.resolved_route_profile)} mono={false} />
            ) : null}
          </PropertyGrid>
        </div>

        {result.resolved_market ? (
          <div>
            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Market
            </p>
            <PropertyGrid cols={2}>
              <PropertyItem label="Name" value={result.resolved_market.name} mono={false} />
              <PropertyItem label="Status" value={result.resolved_market.status} mono={false} />
              <PropertyItem label="Can Sign Up" value={result.resolved_market.can_sign_up ? 'Yes' : 'No'} mono={false} />
              <PropertyItem label="Can Schedule Install" value={result.resolved_market.can_schedule_install ? 'Yes' : 'No'} mono={false} />
              {result.resolved_market.install_method ? (
                <PropertyItem label="Install Method" value={result.resolved_market.install_method} mono={false} />
              ) : null}
              {result.resolved_market.market_group_label ? (
                <PropertyItem label="Market Group" value={result.resolved_market.market_group_label} mono={false} />
              ) : null}
            </PropertyGrid>
          </div>
        ) : null}

        <div>
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Eligibility Outcome
          </p>
          <PropertyGrid cols={2}>
            <PropertyItem label="Status" value={previewStatusLabel(result.status || 'no_coverage')} mono={false} />
            <PropertyItem label="Quote Required" value={result.quote_required ? 'Yes' : 'No'} mono={false} />
            {result.quote_request_reason_code ? (
              <PropertyItem label="Quote Reason" value={result.quote_request_reason_code} mono={false} />
            ) : null}
            {estimate?.estimate_status ? (
              <PropertyItem label="Estimate Status" value={estimate.estimate_status} mono={false} />
            ) : null}
          </PropertyGrid>
        </div>

        {showRouting ? (
          <div>
            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Build Distance
            </p>
            <PropertyGrid cols={2}>
              <PropertyItem label="Signal Status" value={signal?.status || 'Not available'} mono={false} />
              {signal?.reason_code ? <PropertyItem label="Signal Reason" value={signal.reason_code} mono={false} /> : null}
              <PropertyItem label="Nearest Access Point" value={formatRouteEndpointLabel(signal?.nearest_fiber_access_point)} mono={false} />
              <PropertyItem label="Total Build Distance" value={formatDistanceMeters(signal?.total_build_distance_m)} mono={false} />
              <PropertyItem label="Origin Approach" value={formatDistanceMeters(signal?.origin_approach_distance_m)} mono={false} />
              <PropertyItem label="Roadway" value={formatDistanceMeters(signal?.road_distance_m)} mono={false} />
              <PropertyItem label="Access-Point Approach" value={formatDistanceMeters(signal?.destination_approach_distance_m)} mono={false} />
              {signal?.distance_quality ? <PropertyItem label="Distance Quality" value={signal.distance_quality} mono={false} /> : null}
              {signal?.source ? <PropertyItem label="Route Source" value={signal.source} mono={false} /> : null}
            </PropertyGrid>
          </div>
        ) : null}

        {estimate ? (
          <div>
            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Preliminary Estimate
            </p>
            <PropertyGrid cols={2}>
              <PropertyItem label="Estimate Status" value={estimate.estimate_status} mono={false} />
              <PropertyItem label="Quote Required" value={estimate.quote_required ? 'Yes' : 'No'} mono={false} />
              {estimate.reason_code ? <PropertyItem label="Estimate Reason" value={estimate.reason_code} mono={false} /> : null}
              {estimate.price_source ? <PropertyItem label="Price Source" value={estimate.price_source} mono={false} /> : null}
              {estimate.build_type ? <PropertyItem label="Build Type" value={estimate.build_type} mono={false} /> : null}
              {estimate.price_per_meter != null ? (
                <PropertyItem label="Price / m" value={formatCurrencyAmount(estimate.price_per_meter, estimate.currency)} mono={false} />
              ) : null}
              {estimate.base_price_per_meter != null ? (
                <PropertyItem label="Base Price / m" value={formatCurrencyAmount(estimate.base_price_per_meter, estimate.currency)} mono={false} />
              ) : null}
              {estimate.estimated_quote_amount != null ? (
                <PropertyItem label="Estimated Quote" value={formatCurrencyAmount(estimate.estimated_quote_amount, estimate.currency)} mono={false} />
              ) : null}
              {estimate.disclaimer ? <PropertyItem label="Disclaimer" value={estimate.disclaimer} mono={false} /> : null}
            </PropertyGrid>
          </div>
        ) : null}
      </VerticalStack>
    </Surface>
  );
}

function CatalogResolutionPanel({
  result,
  publicationLookup,
  channelLookup,
}: {
  result: AdminEligibilityPreviewResponse;
  publicationLookup: Map<string, CommerceSelectOptionViewModel>;
  channelLookup: Map<string, CommerceSelectOptionViewModel>;
}) {
  return (
    <Surface className="border-border/50 p-5 shadow-sm">
      <VerticalStack gap={5}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 bg-blue-500/10 text-blue-700 ring-blue-500/20">
            <Package className="h-3 w-3" />
            Publication
          </span>
          <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ring-1 bg-muted text-muted-foreground ring-border/40">
            <DollarSign className="h-3 w-3" />
            {result.resolved_commercial_context.pricebook_id ? 'Pricebook Assigned' : 'Pricebook Missing'}
          </span>
        </div>

        <div>
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Publication &amp; Channel
          </p>
          <PropertyGrid cols={2}>
            <PropertyItem
              label="Publication"
              value={
                result.resolved_publication.storefront_key ||
                publicationLookup.get(result.resolved_publication.publication_id)?.label ||
                result.resolved_publication.publication_id
              }
              mono={false}
            />
            <PropertyItem label="Catalog Key" value={result.resolved_publication.catalog_key || '—'} mono={false} />
            <PropertyItem label="Pub Scope" value={result.resolved_publication.scope || '—'} mono={false} />
            <PropertyItem label="Pub Status" value={result.resolved_publication.status || '—'} mono={false} />
            <PropertyItem
              label="Channel"
              value={
                result.resolved_channel.name ||
                channelLookup.get(result.resolved_channel.channel_id)?.label ||
                result.resolved_channel.channel_id
              }
              mono={false}
            />
            <PropertyItem label="Channel Kind" value={result.resolved_channel.kind || '—'} mono={false} />
            <PropertyItem label="Channel Status" value={result.resolved_channel.status || '—'} mono={false} />
            <PropertyItem label="Binding" value={result.resolved_channel.binding_status || '—'} mono={false} />
          </PropertyGrid>
        </div>

        <div>
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Commercial Context
          </p>
          <PropertyGrid cols={2}>
            <PropertyItem
              label="Pricebook"
              value={result.resolved_commercial_context.pricebook_id || 'Not assigned'}
              mono={false}
            />
            <PropertyItem
              label="Commission Plan"
              value={result.resolved_commercial_context.commission_plan_id || 'Not assigned'}
              mono={false}
            />
            {result.resolved_commercial_context.selling_tenant_id ? (
              <PropertyItem label="Selling Tenant" value={result.resolved_commercial_context.selling_tenant_id} mono={false} />
            ) : null}
            {result.resolved_commercial_context.store_id ? (
              <PropertyItem label="Store" value={result.resolved_commercial_context.store_id} mono={false} />
            ) : null}
            {result.resolved_commercial_context.agent_user_id ? (
              <PropertyItem label="Agent" value={result.resolved_commercial_context.agent_user_id} mono={false} />
            ) : null}
          </PropertyGrid>
        </div>
      </VerticalStack>
    </Surface>
  );
}

export default function StorefrontsPage() {
  const { activeTenant, organizations } = useScope();
  const navigation = useNavigation();

  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [publications, setPublications] = useState<CatalogPublication[]>([]);
  const [channels, setChannels] = useState<CommerceChannel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [groups, setGroups] = useState<MarketGroup[]>([]);
  const [pricebooks, setPricebooks] = useState<Pricebook[]>([]);
  const [storefronts, setStorefronts] = useState<StorefrontExperience[]>([]);
  const [hasRoadDistanceAccessPoints, setHasRoadDistanceAccessPoints] = useState(false);
  const [hasRoutePricingDefaults, setHasRoutePricingDefaults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [selectedExperienceId, setSelectedExperienceId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorActiveTab, setEditorActiveTab] = useState('identity');
  const [editorMode, setEditorMode] = useState<StorefrontEditorMode>('create');
  const [rulesWizardOpen, setRulesWizardOpen] = useState(false);
  const [ruleEditorOpen, setRuleEditorOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<EligibilityPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [validatingExperienceId, setValidatingExperienceId] = useState<string | null>(null);
  const [repairingAction, setRepairingAction] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState('parameters');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<AdminEligibilityPreviewResponse | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['segment', 'status'],
    facetValues: {
      segment: ['business', 'residential'],
      status: ['archived', 'draft', 'ready'],
    },
    sortFields: STOREFRONT_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalStorefronts, setTotalStorefronts] = useState(0);
  const [storefrontFacetCounts, setStorefrontFacetCounts] = useState<Record<string, Record<string, number>>>({});
  const requestSequence = useRef(0);
  const storefrontColumnFilters = useMemo<ColumnFiltersState>(() => [
    ...(query.facets.segment.length > 0 ? [{ id: 'segment', value: query.facets.segment }] : []),
    ...(query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : []),
  ], [query.facets.segment, query.facets.status]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [form, setForm] = useState<StorefrontFormState>(createEmptyForm);
  const [previewForm, setPreviewForm] = useState<StorefrontPreviewFormState>(createPreviewForm(null));
  const [principalRows, setPrincipalRows] = useState<StorefrontPrincipalRow[]>(principalRowsFromExperience(null));
  const [bindingRows, setBindingRows] = useState<StorefrontPublicationBindingRow[]>([]);
  const [bindingsDirty, setBindingsDirty] = useState(false);
  const [bindingsLoading, setBindingsLoading] = useState(false);
  const [bindingsSaving, setBindingsSaving] = useState(false);
  const [bindingsError, setBindingsError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const loadData = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      setCatalogs([]);
      setPublications([]);
      setChannels([]);
      setProducts([]);
      setMarkets([]);
      setGroups([]);
      setPricebooks([]);
      setStorefronts([]);
      setTotalStorefronts(0);
      setStorefrontFacetCounts({});
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }

    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);

    try {
      const [
        nextCatalogs,
        nextPublications,
        nextChannels,
        selectorRecords,
        nextPricebooks,
        nextStorefronts,
        quoteEstimateAccessPoints,
        businessQuoteAccessPoints,
      ] = await Promise.all([
        listCatalogs({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listPublications({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listCommerceChannels({ catalogOwnerTenantId: activeTenant.id }),
        loadCommerceSelectorRecords(activeTenant.id),
        listPricebooks({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listStorefrontExperiences({
          catalogOwnerTenantId: activeTenant.id,
          search: searchQuery || undefined,
          segment: query.facets.segment,
          status: query.facets.status,
          sort: query.sort,
          include_facets: ['segment', 'status'],
          ...toLimitOffset(query.pagination),
        }),
        listAccessPointLayers({ catalogOwnerTenantId: activeTenant.id, status: 'active', purpose: 'quote_estimate', limit: 1, offset: 0 }),
        listAccessPointLayers({ catalogOwnerTenantId: activeTenant.id, status: 'active', purpose: 'business_quote_estimate', limit: 1, offset: 0 }),
      ]);

      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextStorefronts.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }

      setCatalogs(nextCatalogs || []);
      setPublications(nextPublications || []);
      setChannels(nextChannels || []);
      setProducts(selectorRecords.products || []);
      setMarkets(selectorRecords.markets || []);
      setGroups(selectorRecords.groups || []);
      setPricebooks(nextPricebooks || []);
      setStorefronts(nextStorefronts.items);
      setTotalStorefronts(nextStorefronts.total_count);
      setStorefrontFacetCounts(Object.fromEntries(
        Object.entries(nextStorefronts.facets).map(([key, buckets]) => [
          key,
          Object.fromEntries(buckets.map(bucket => [bucket.value, bucket.count])),
        ]),
      ));
      const routePricingLayers = [...(quoteEstimateAccessPoints.items || []), ...(businessQuoteAccessPoints.items || [])];
      setHasRoadDistanceAccessPoints(routePricingLayers.length > 0);
      setHasRoutePricingDefaults(routePricingLayers.some(layer => (layer.default_build_type_rates?.length ?? 0) > 0));
      setSelectedExperienceId(current => (
        current && nextStorefronts.items.some(item => item.experience_id === current) ? current : null
      ));
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load storefronts.'));
      }
      setCatalogs([]);
      setPublications([]);
      setChannels([]);
      setProducts([]);
      setMarkets([]);
      setGroups([]);
      setPricebooks([]);
      setStorefronts([]);
      setTotalStorefronts(0);
      setStorefrontFacetCounts({});
      setHasRoadDistanceAccessPoints(false);
      setHasRoutePricingDefaults(false);
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [activeTenant?.id, query.facets.segment, query.facets.status, query.pagination, query.sort, searchQuery]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setBindingsDirty(false);
    if (!editorOpen || form.autoCreateChannel || !form.channelId) {
      setBindingRows([]);
      setBindingsError(null);
      return;
    }
    let cancelled = false;
    setBindingsLoading(true);
    setBindingsError(null);
    void listCommerceChannelPublications(form.channelId)
      .then(items => {
        if (cancelled) return;
        setBindingRows(items.map(channelPublicationToBindingRow));
      })
      .catch(err => {
        if (cancelled) return;
        setBindingRows([]);
        setBindingsError(getCommerceErrorMessage(err, 'Failed to load publication routes.'));
      })
      .finally(() => {
        if (!cancelled) setBindingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editorOpen, form.autoCreateChannel, form.channelId]);

  useEffect(() => {
    const firstKey = Object.keys(rowSelection)[0];
    if (firstKey === undefined) {
      return;
    }
    const index = Number(firstKey);
    if (!Number.isFinite(index)) {
      return;
    }
    const nextSelected = storefronts[index];
    if (nextSelected) {
      setSelectedExperienceId(nextSelected.experience_id);
    }
  }, [rowSelection, storefronts]);

  const selectedExperience = useMemo(
    () => storefronts.find(item => item.experience_id === selectedExperienceId) || null,
    [selectedExperienceId, storefronts],
  );

  const catalogMap = useMemo(() => new Map(catalogs.map(item => [item.catalog_id, item])), [catalogs]);
  const publicationMap = useMemo(() => new Map(publications.map(item => [item.publication_id, item])), [publications]);
  const channelMap = useMemo(() => new Map(channels.map(item => [item.channel_id, item])), [channels]);
  const publicationOptions = useMemo(() => buildPublicationSelectorOptions(publications), [publications]);
  const channelOptions = useMemo(() => buildChannelSelectorOptions(channels), [channels]);
  const productOptions = useMemo(() => buildProductSelectorOptions(products, catalogMap), [products, catalogMap]);
  const variantOptions = useMemo(() => buildVariantSelectorOptions(products, catalogMap), [products, catalogMap]);
  const marketOptions = useMemo(() => buildMarketSelectorOptions(markets), [markets]);
  const segmentOptions = useMemo(() => buildMarketGroupSelectorOptions(groups), [groups]);
  const sellingTenantOptions = useMemo(
    () =>
      buildTenantSelectorOptions({
        activeTenant,
        organizations,
        observedTenantIds: publications.flatMap(publication => publication.selling_tenant_ids || []),
      }),
    [activeTenant, organizations, publications],
  );
  const storeOptions = useMemo(() => buildObservedScopeOptions(publications, 'stores'), [publications]);
  const agentOptions = useMemo(() => buildObservedScopeOptions(publications, 'agents'), [publications]);
  const serviceClassOptions = useMemo(() => buildServiceClassSelectorOptions(products), [products]);
  const serviceGroupOptions = useMemo(() => buildServiceGroupSelectorOptions(products), [products]);
  const publicationLookup = useMemo(() => buildOptionLookup(publicationOptions), [publicationOptions]);
  const channelLookup = useMemo(() => buildOptionLookup(channelOptions), [channelOptions]);
  const productOptionLookup = useMemo(() => buildOptionLookup(productOptions), [productOptions]);
  const variantOptionLookup = useMemo(() => buildOptionLookup(variantOptions), [variantOptions]);
  const marketOptionLookup = useMemo(() => buildOptionLookup(marketOptions), [marketOptions]);
  const segmentOptionLookup = useMemo(() => buildOptionLookup(segmentOptions), [segmentOptions]);
  const tenantOptionLookup = useMemo(() => buildOptionLookup(sellingTenantOptions), [sellingTenantOptions]);
  const storeOptionLookup = useMemo(() => buildOptionLookup(storeOptions), [storeOptions]);
  const agentOptionLookup = useMemo(() => buildOptionLookup(agentOptions), [agentOptions]);
  const serviceClassOptionLookup = useMemo(() => buildOptionLookup(serviceClassOptions), [serviceClassOptions]);
  const serviceGroupOptionLookup = useMemo(() => buildOptionLookup(serviceGroupOptions), [serviceGroupOptions]);

  const catalogOptions = useMemo(
    () =>
      catalogs.map(catalog => ({
        value: catalog.catalog_id,
        label: catalog.display_name || catalog.name || catalog.catalog_key,
        description: catalog.catalog_key,
        helperText: catalog.status,
      })),
    [catalogs],
  );

  const upsertLocalExperience = (item: StorefrontExperience) => {
    setStorefronts(current =>
      current.some(existing => existing.experience_id === item.experience_id)
        ? current.map(existing => (existing.experience_id === item.experience_id ? item : existing))
        : [item, ...current],
    );
    setSelectedExperienceId(item.experience_id);
  };

  const openCreate = () => {
    setEditorMode('create');
    setEditorActiveTab('identity');
    setForm(createEmptyForm());
    setPrincipalRows(principalRowsFromExperience(null));
    setBindingRows([]);
    setBindingsError(null);
    setValidationErrors({});
    setEditorOpen(true);
  };

  const openEdit = (item: StorefrontExperience) => {
    setEditorMode('edit');
    setEditorActiveTab('identity');
    setSelectedExperienceId(item.experience_id);
    setForm({
      name: item.name,
      experienceKey: item.experience_key,
      segment: item.segment,
      eligibilityMode: item.eligibility_mode || (item.segment === 'business' ? 'zone_and_road_distance' : 'zone_only'),
      routeProfile: item.route_profile || 'fiber_construction',
      runtimeFamily: item.runtime_family,
      status: item.status,
      storefrontKey: item.storefront_key,
      channelId: item.channel_id || '',
      confirmationRedirectUrl: typeof item.metadata?.order_confirmation_redirect_url === 'string'
        ? item.metadata.order_confirmation_redirect_url
        : '',
      autoCreateChannel: !item.channel_id,
    });
    setPrincipalRows(principalRowsFromExperience(item));
    setBindingRows([]);
    setBindingsError(null);
    setValidationErrors({});
    setEditorOpen(true);
  };

  const openPreview = (item: StorefrontExperience) => {
    setSelectedExperienceId(item.experience_id);
    setPreviewForm(createPreviewForm(item));
    setPreviewError(null);
    setPreviewResult(null);
    setPreviewTab('parameters');
    setPreviewOpen(true);
  };

  const openRules = (item: StorefrontExperience) => {
    query.clear();
    setSelectedExperienceId(item.experience_id);
    setRulesWizardOpen(true);
  };

  const closeRules = () => {
    setRulesWizardOpen(false);
    setRuleEditorOpen(false);
    setEditingPolicy(null);
    setSelectedExperienceId(null);
  };

  const openRuleEditor = useCallback((policy?: EligibilityPolicy) => {
    setEditingPolicy(policy ?? null);
    setRuleEditorOpen(true);
  }, []);

  const closeRuleEditor = useCallback(() => {
    setRuleEditorOpen(false);
    setEditingPolicy(null);
  }, []);

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewTab('parameters');
    setPreviewError(null);
    setPreviewResult(null);
  };

  const handleDelete = useCallback(async (item: StorefrontExperience) => {
    if (!window.confirm(`Archive storefront "${item.name}"?`)) {
      return;
    }

    setError(null);
    try {
      await deleteStorefrontExperience(item.experience_id);
      await loadData();
      toast.success('Storefront archived', {
        description: item.name,
      });
      if (selectedExperienceId === item.experience_id) {
        setSelectedExperienceId(null);
      }
      if (selectedExperience?.experience_id === item.experience_id) {
        setEditorOpen(false);
        setRulesWizardOpen(false);
        closePreview();
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const message = getCommerceErrorMessage(err, 'Failed to archive storefront.');
        setError(message);
        toast.error('Failed to archive storefront', { description: message });
      }
    }
  }, [closePreview, loadData, selectedExperience, selectedExperienceId]);

  const handleSave = async () => {
    if (bindingsLoading || bindingsSaving || saving) return;
    const errors = validateForm(form, principalRows);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setSaving(true);
    try {
      if (bindingsDirty && !(await persistBindings())) {
        setEditorActiveTab('binding');
        return;
      }
      const metadata = editorMode === 'edit' ? { ...(selectedExperience?.metadata || {}) } : {};
      if (form.confirmationRedirectUrl.trim()) {
        metadata.order_confirmation_redirect_url = form.confirmationRedirectUrl.trim();
      } else {
        delete metadata.order_confirmation_redirect_url;
      }
      const payload = {
        name: form.name.trim(),
        experience_key: form.experienceKey.trim(),
        segment: form.segment,
        eligibility_mode: form.eligibilityMode,
        route_profile: form.routeProfile,
        runtime_family: form.runtimeFamily,
        status: form.status,
        storefront_key: form.storefrontKey.trim() || form.experienceKey.trim(),
        channel_id: form.autoCreateChannel ? undefined : form.channelId || undefined,
        auto_create_channel: form.autoCreateChannel,
        principal_defaults: principalRows
          .filter(row => row.principalType.trim() && row.principalId.trim())
          .map(row => ({
            principal_type: row.principalType.trim(),
            principal_id: row.principalId.trim(),
          })),
        metadata,
      };

      const saved = editorMode === 'edit' && selectedExperience
        ? await updateStorefrontExperience(selectedExperience.experience_id, payload)
        : await createStorefrontExperience(payload);
      upsertLocalExperience(saved);
      setEditorOpen(false);
      toast.success(editorMode === 'edit' ? 'Storefront updated' : 'Storefront created', {
        description: `Storefront ${saved.experience_key} is ready for review.`,
      });
      await loadData();
    } catch (err) {
      toast.error('Save failed', {
        description: getCommerceErrorMessage(err, 'Failed to save storefront.'),
      });
    } finally {
      setSaving(false);
    }
  };

  const persistBindings = async (): Promise<boolean> => {
    if (!form.channelId) return false;
    const activePrimaryCount = bindingRows.filter(row => row.bindingRole === 'primary' && row.status === 'active').length;
    if (activePrimaryCount !== 1) {
      setBindingsError('Exactly one active default publication is required.');
      return false;
    }
    if (bindingRows.some(row => !row.publicationId || (row.bindingRole === 'secondary' && row.targetType !== 'all' && !row.targetId))) {
      setBindingsError('Choose a publication and complete every conditional market target.');
      return false;
    }
    setBindingsSaving(true);
    setBindingsError(null);
    try {
      const saved = await replaceCommerceChannelPublications(form.channelId, bindingRows.map(row => ({
        publication_id: row.publicationId,
        binding_role: row.bindingRole,
        status: row.status,
        priority: row.priority,
        audience: row.audience,
        market_id: row.targetType === 'market' ? row.targetId : null,
        market_group_id: row.targetType === 'market_group' ? row.targetId : null,
      })));
      setBindingRows(saved.map(channelPublicationToBindingRow));
      setBindingsDirty(false);
      return true;
    } catch (err) {
      setBindingsError(getCommerceErrorMessage(err, 'Failed to save publication routes.'));
      return false;
    } finally {
      setBindingsSaving(false);
    }
  };

  const handleValidate = async (item: StorefrontExperience) => {
    setValidatingExperienceId(item.experience_id);
    try {
      const validated = await validateStorefrontExperience(item.experience_id);
      upsertLocalExperience(validated);
      toast.success('Readiness refreshed', {
        description: `Storefront ${validated.experience_key} was revalidated.`,
      });
    } catch (err) {
      toast.error('Validation failed', {
        description: getCommerceErrorMessage(err, 'Failed to validate storefront.'),
      });
    } finally {
      setValidatingExperienceId(null);
    }
  };

  const handleRepair = async (item: StorefrontExperience, action: string) => {
    if (!action.trim()) {
      return;
    }
    setRepairingAction(`${item.experience_id}:${action}`);
    try {
      const repaired = await repairStorefrontExperience(item.experience_id, [action]);
      upsertLocalExperience(repaired);
      toast.success('Repair applied', {
        description: `Applied ${action} to ${repaired.experience_key}.`,
      });
      await loadData();
    } catch (err) {
      toast.error('Repair failed', {
        description: getCommerceErrorMessage(err, 'Failed to repair storefront.'),
      });
    } finally {
      setRepairingAction(null);
    }
  };

  const handleManageAccessPoints = useCallback(() => {
    navigation.navigate('/commerce/access-points');
  }, [navigation]);

  const previewDependencyWarnings = useMemo(() => {
    if (previewForm.eligibilityMode === 'zone_only') {
      return [];
    }
    const warnings: string[] = [];
    if (!hasRoadDistanceAccessPoints) {
      warnings.push('No active quote-estimate access point layer is available for this tenant.');
    }
    if (!hasRoutePricingDefaults) {
      warnings.push('No active quote-estimate access point layer has default route pricing configured for this tenant.');
    }
    return warnings;
  }, [previewForm.eligibilityMode, hasRoadDistanceAccessPoints, hasRoutePricingDefaults]);
  const previewNeedsAccessPointPricing = previewDependencyWarnings.some(warning => warning.toLowerCase().includes('access point'));

  const handleRunPreview = async () => {
    if (!selectedExperience?.publication_id || !selectedExperience.channel_id) {
      setPreviewError('This storefront needs a linked publication and channel before preview can run.');
      setPreviewTab('parameters');
      return;
    }
    if (!previewForm.address.trim()) {
      setPreviewError('Address is required.');
      setPreviewTab('parameters');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await previewEligibility({
        address: previewForm.address.trim(),
        google_place_id: previewForm.googlePlaceId || undefined,
        lat: previewForm.lat ?? undefined,
        lng: previewForm.lng ?? undefined,
        place_data: previewForm.placeData ?? undefined,
        publication_id: selectedExperience.publication_id,
        channel_id: selectedExperience.channel_id,
        eligibility_mode: previewForm.eligibilityMode,
        route_profile: previewForm.eligibilityMode === 'zone_only' ? undefined : previewForm.routeProfile,
        build_type: toOptionalString(previewForm.buildType),
        segment: selectedExperience.segment,
        business: selectedExperience.segment === 'business',
        selling_tenant_id: toOptionalString(previewForm.sellingTenantId),
        store_id: toOptionalString(previewForm.storeId),
        agent_user_id: toOptionalString(previewForm.agentUserId),
        include_drafts: previewForm.includeDrafts,
      });
      setPreviewResult(result);
      setPreviewTab('results');
    } catch (err) {
      setPreviewError(getCommerceErrorMessage(err, 'Failed to preview storefront eligibility.'));
      setPreviewResult(null);
      setPreviewTab('parameters');
    } finally {
      setPreviewLoading(false);
    }
  };

  const previewSellableById = useMemo(() => {
    const lookup = new Map<string, AdminEligibilityPreviewProduct>();
    for (const product of previewResult?.sellable_products || []) {
      if (product.product_id) {
        lookup.set(product.product_id, product);
      }
      if (product.product_type_id) {
        lookup.set(product.product_type_id, product);
      }
    }
    return lookup;
  }, [previewResult]);

  const previewBlockedById = useMemo(() => {
    const lookup = new Map<string, AdminEligibilityPreviewProductBlock>();
    for (const product of previewResult?.blocked_products || []) {
      if (product.product_id) {
        lookup.set(product.product_id, product);
      }
      if (product.product_type_id) {
        lookup.set(product.product_type_id, product);
      }
    }
    return lookup;
  }, [previewResult]);

  const diagnosticsByProductId = useMemo<DiagnosticsIndex>(() => {
    const index: DiagnosticsIndex = new Map();
    for (const diagnostic of previewResult?.diagnostics || []) {
      const key = diagnostic.product_id || null;
      if (!index.has(key)) {
        index.set(key, []);
      }
      index.get(key)?.push(diagnostic);
    }
    return index;
  }, [previewResult]);

  const metrics = useMemo(() => [
    {
      id: 'storefront-total',
      label: 'Storefronts',
      value: String(totalStorefronts),
    },
    {
      id: 'storefront-ready',
      label: 'Ready',
      value: String(storefrontFacetCounts.status?.ready ?? 0),
      tone: 'success' as const,
    },
    {
      id: 'storefront-attention',
      label: 'Attention',
      value: String(Math.max(0, totalStorefronts - (storefrontFacetCounts.status?.ready ?? 0))),
      tone: totalStorefronts > (storefrontFacetCounts.status?.ready ?? 0) ? 'warning' as const : 'default' as const,
    },
  ], [storefrontFacetCounts.status, totalStorefronts]);

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'storefronts',
    searchQuery,
    searchPlaceholder: 'Search storefronts by name, key, segment, or status...',
    onSearchChange: query.setSearch,
    error,
    emptyStateLabel: 'Select a tenant to manage storefront runtime setup.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'new-storefront',
      label: 'Create Storefront',
      onAction: openCreate,
      disabled: !activeTenant?.id,
    },
    metrics,
  });

  const previewSummaryItems = useMemo(() => {
    if (!previewResult) {
      return [{ label: 'Status', value: 'Not run yet', complete: false }];
    }
    return [
      { label: 'Status', value: previewStatusLabel(previewResult.status || 'no_coverage'), complete: previewResult.status === 'serviceable' || previewResult.status === 'quote_required' },
      { label: 'Eligibility', value: previewResult.quote_required ? 'Quote required' : 'Direct checkout', complete: !previewResult.quote_required },
      { label: 'Sellable', value: `${previewResult.sellable_products.length}`, complete: previewResult.sellable_products.length > 0 },
      { label: 'Blocked', value: `${previewResult.blocked_products.length}`, complete: previewResult.blocked_products.length === 0 },
      { label: 'Market', value: previewResult.resolved_market?.name || 'Not resolved', complete: Boolean(previewResult.resolved_market?.name) },
    ];
  }, [previewResult]);

  const previewSummaryCallout = useMemo(() => {
    if (!previewResult?.warnings.length) {
      return null;
    }
    return `${previewResult.warnings.length} warning${previewResult.warnings.length === 1 ? '' : 's'} detected. Check the Diagnostics tab.`;
  }, [previewResult]);

  const previewTabs = useMemo<WizardTab[]>(() => [
    { id: 'parameters', label: 'Parameters', icon: <Search className="h-4 w-4" /> },
    {
      id: 'results',
      label: 'Results',
      icon: <Activity className="h-4 w-4" />,
      count: previewResult ? previewResult.sellable_products.length + previewResult.blocked_products.length : undefined,
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      icon: <Info className="h-4 w-4" />,
      count: previewResult ? previewResult.diagnostics.length + previewResult.warnings.length : undefined,
    },
  ], [previewResult]);

  const previewWorkspace = useMemo(() => {
    if (!previewOpen) return null;
    return (
      <WizardWorkspace
        isOpen={previewOpen}
        isLoading={false}
        isSaving={previewLoading}
        title={selectedExperience ? `Preview ${selectedExperience.name}` : 'Preview Eligibility'}
        subtitle="Resolve an address against the selected storefront runtime to inspect the effective storefront product set."
        tabs={previewTabs}
        activeTabId={previewTab}
        onTabChange={setPreviewTab}
        saveLabel="Run Preview"
        discardLabel="Close"
        onSave={() => void handleRunPreview()}
        onClose={closePreview}
        summaryEyebrow="Preview Results"
        summaryItems={previewSummaryItems}
        summaryCallout={previewSummaryCallout}
      >
        <VerticalStack gap={8}>
          {previewTab === 'parameters' ? (
            <section>
              <SectionHeading
                title="Preview Parameters"
                subtitle="Enter the service address and optional commercial context. Publication and channel come from the selected storefront."
              />
              <Surface className="mt-4 p-6 border-border/60">
                <VerticalStack gap={6}>
                  <Field label="Storefront Runtime" description="This preview uses the selected storefront experience as the source of truth.">
                    <PropertyGrid cols={2}>
                      <PropertyItem label="Storefront" value={selectedExperience?.name || 'No storefront selected'} mono={false} />
                      <PropertyItem label="Experience Key" value={selectedExperience?.experience_key || '—'} mono={false} />
                      <PropertyItem
                        label="Publication"
                        value={
                          selectedExperience?.publication_id
                            ? (publicationMap.get(selectedExperience.publication_id)?.storefront_key || selectedExperience.publication_id)
                            : 'Missing'
                        }
                        mono={false}
                      />
                      <PropertyItem
                        label="Channel"
                        value={
                          selectedExperience?.channel_id
                            ? (channelMap.get(selectedExperience.channel_id)?.channel_key || selectedExperience.channel_id)
                            : 'Missing'
                        }
                        mono={false}
                      />
                    </PropertyGrid>
                  </Field>

                  <Field label="Service Address" description="Use the customer-facing address you want to test.">
                    <CommerceEligibilityAddressInput
                      value={previewForm.address}
                      googlePlaceId={previewForm.googlePlaceId || undefined}
                      disabled={previewLoading}
                      onValueChange={address =>
                        setPreviewForm(current => ({
                          ...current,
                          address,
                          googlePlaceId: '',
                          lat: null,
                          lng: null,
                          placeData: null,
                        }))
                      }
                      onSelect={selection =>
                        setPreviewForm(current => ({
                          ...current,
                          address: selection.address,
                          googlePlaceId: selection.googlePlaceId || '',
                          lat: selection.lat ?? null,
                          lng: selection.lng ?? null,
                          placeData: selection.placeData ?? null,
                        }))
                      }
                      onClearSelection={() =>
                        setPreviewForm(current => ({
                          ...current,
                          googlePlaceId: '',
                          lat: null,
                          lng: null,
                          placeData: null,
                        }))
                      }
                    />
                  </Field>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field label="Eligibility Type" description="Adjust the storefront strategy for this preview run.">
                      <CommerceCombobox
                        options={[
                          { value: 'zone_only', label: 'Zone Only' },
                          { value: 'zone_and_road_distance', label: 'Zone + Road Distance' },
                          { value: 'road_distance_only', label: 'Road Distance Only' },
                        ]}
                        value={previewForm.eligibilityMode}
                        onChange={eligibilityMode => setPreviewForm(current => ({ ...current, eligibilityMode }))}
                        placeholder="Choose eligibility type"
                        searchPlaceholder="Search eligibility types..."
                        emptyMessage="No eligibility types are available."
                      />
                    </Field>
                    <Field label="Route Profile" description="Used when the preview relies on road-distance routing.">
                      <CommerceCombobox
                        options={[{ value: 'fiber_construction', label: 'Fiber Construction' }]}
                        value={previewForm.routeProfile}
                        onChange={routeProfile => setPreviewForm(current => ({ ...current, routeProfile }))}
                        placeholder="Choose route profile"
                        searchPlaceholder="Search route profiles..."
                        emptyMessage="No route profiles are available."
                      />
                    </Field>
                    <Field label="Build Type" description="Optional route build type. Required when the active access-point defaults or pricing zones expose multiple build-type rates.">
                      <input
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={previewForm.buildType}
                        onChange={event => setPreviewForm(current => ({ ...current, buildType: event.target.value }))}
                        placeholder="e.g. aerial"
                      />
                    </Field>
                    <Field label="Selling Tenant" description="Optional reseller or organization context for scoped commercial terms.">
                      <CommerceCombobox
                        options={sellingTenantOptions}
                        value={previewForm.sellingTenantId}
                        onChange={sellingTenantId => setPreviewForm(current => ({ ...current, sellingTenantId }))}
                        placeholder="Optional selling tenant"
                        searchPlaceholder="Search selling tenants..."
                        emptyMessage="No selling tenants are available yet."
                      />
                    </Field>
                    <Field label="Store" description="Optional store context for scoped assignments.">
                      <CommerceCombobox
                        options={storeOptions}
                        value={previewForm.storeId}
                        onChange={storeId => setPreviewForm(current => ({ ...current, storeId }))}
                        placeholder="Optional store"
                        searchPlaceholder="Search stores..."
                        emptyMessage="No stores are available yet."
                      />
                    </Field>
                    <Field label="Agent" description="Optional agent context for scoped assignments.">
                      <CommerceCombobox
                        options={agentOptions}
                        value={previewForm.agentUserId}
                        onChange={agentUserId => setPreviewForm(current => ({ ...current, agentUserId }))}
                        placeholder="Optional agent"
                        searchPlaceholder="Search agents..."
                        emptyMessage="No agents are available yet."
                      />
                    </Field>
                    <Field
                      label="Include Drafts"
                      description="Operator-only preview mode for draft publication, catalog, product, variant, or offer state."
                    >
                      <label className="flex items-center gap-3 rounded-md border border-border/60 bg-muted/10 px-3 py-2 text-sm font-bold text-foreground">
                        <Checkbox
                          checked={previewForm.includeDrafts}
                          onCheckedChange={value => setPreviewForm(current => ({ ...current, includeDrafts: Boolean(value) }))}
                          aria-label="Include drafts"
                        />
                        Include drafts in preview
                      </label>
                    </Field>
                  </div>

                  {previewDependencyWarnings.length > 0 ? (
                    <Callout variant="warning" className="border-amber-300/50 bg-amber-500/5">
                      <VerticalStack gap={2}>
                        <p className="text-sm font-black text-foreground">Road-distance preview dependencies need attention</p>
                        {previewDependencyWarnings.map(warning => (
                          <DescriptionText key={warning} className="text-xs">
                            {warning}
                          </DescriptionText>
                        ))}
                        {previewNeedsAccessPointPricing ? (
                          <div>
                            <button
                              type="button"
                              onClick={handleManageAccessPoints}
                              className="inline-flex h-9 items-center rounded-md border border-amber-400/40 bg-background px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-amber-50"
                            >
                              Manage Access Points
                            </button>
                          </div>
                        ) : null}
                      </VerticalStack>
                    </Callout>
                  ) : null}

                  {previewError ? (
                    <DescriptionText className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                      {previewError}
                    </DescriptionText>
                  ) : null}
                </VerticalStack>
              </Surface>
            </section>
          ) : null}

          {previewTab === 'results' ? (
            <section>
              <SectionHeading
                title="Preview Report"
                subtitle="Eligibility explains serviceability and quote-required outcomes. Catalog Resolution explains which publication, channel, and commercial context were evaluated."
              />

              {!previewResult && !previewLoading ? (
                <Surface className="mt-4 border-border/40 bg-muted/5 p-10">
                  <VerticalStack gap={3} className="items-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Eye className="h-6 w-6 text-primary/60" />
                    </div>
                    <p className="text-sm font-black text-foreground">No preview run yet</p>
                    <DescriptionText className="max-w-sm text-xs">
                      Go to the Parameters tab, fill in the required fields, and click Run Preview to see results.
                    </DescriptionText>
                    <button
                      type="button"
                      onClick={() => setPreviewTab('parameters')}
                      className="mt-1 inline-flex items-center gap-2 rounded-md border border-border/50 px-4 py-2 text-xs font-black text-foreground transition hover:bg-muted/40"
                    >
                      Go to Parameters
                    </button>
                  </VerticalStack>
                </Surface>
              ) : null}

              {previewLoading ? (
                <VerticalStack gap={3} className="mt-4">
                  <Surface className="h-14 animate-pulse border-border/40 bg-muted/10" />
                  <Surface className="h-32 animate-pulse border-border/40 bg-muted/10" />
                  <Surface className="h-32 animate-pulse border-border/40 bg-muted/10" />
                </VerticalStack>
              ) : null}

              {previewResult && !previewLoading ? (
                <VerticalStack gap={3} className="mt-4">
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <div>
                      <SectionHeading
                        title="Eligibility"
                        subtitle="Serviceability outcome, route-distance evaluation, and quote-required reasoning."
                      />
                      <div className="mt-3">
                        <EligibilityOutcomePanel result={previewResult} />
                      </div>
                    </div>
                    <div>
                      <SectionHeading
                        title="Catalog Resolution"
                        subtitle="Resolved publication, channel, and commercial context for this storefront preview."
                      />
                      <div className="mt-3">
                        <CatalogResolutionPanel
                          result={previewResult}
                          publicationLookup={publicationLookup}
                          channelLookup={channelLookup}
                        />
                      </div>
                    </div>
                  </div>

                  <SectionHeading
                    title="Publication Result Set"
                    subtitle="Sellable and blocked products are grouped by publication section. Expand any section or product card to see variants, block reasons, policy IDs, and linked diagnostics."
                  />

                  {previewResult.sections.length === 0 ? (
                    previewResult.sellable_products.length > 0 || previewResult.blocked_products.length > 0 ? (
                      <PreviewUngroupedResultsPanel
                        sellableProducts={previewResult.sellable_products}
                        blockedProducts={previewResult.blocked_products}
                        diagnosticsByProductId={diagnosticsByProductId}
                      />
                    ) : (
                      <RegistryEmptyState
                        icon={<Eye className="h-6 w-6" />}
                        title="No section report available"
                        description="The selected publication does not expose any sections for this preview result."
                      />
                    )
                  ) : (
                    previewResult.sections.map(section => (
                      <PreviewSectionPanel
                        key={section.section_id}
                        section={section}
                        sellableById={previewSellableById}
                        blockedById={previewBlockedById}
                        diagnosticsByProductId={diagnosticsByProductId}
                      />
                    ))
                  )}
                </VerticalStack>
              ) : null}
            </section>
          ) : null}

          {previewTab === 'diagnostics' ? (
            <section>
              <SectionHeading
                title="Diagnostics & Warnings"
                subtitle="Diagnostics are grouped by severity. Product-linked diagnostics also appear inline on blocked product cards in the Results tab."
              />

              {!previewResult && !previewLoading ? (
                <Surface className="mt-4 border-border/40 bg-muted/5 p-10">
                  <VerticalStack gap={3} className="items-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Info className="h-6 w-6 text-primary/60" />
                    </div>
                    <p className="text-sm font-black text-foreground">No preview run yet</p>
                    <DescriptionText className="max-w-sm text-xs">
                      Go to the Parameters tab, fill in the required fields, and click Run Preview to see diagnostic info.
                    </DescriptionText>
                    <button
                      type="button"
                      onClick={() => setPreviewTab('parameters')}
                      className="mt-1 inline-flex items-center gap-2 rounded-md border border-border/50 px-4 py-2 text-xs font-black text-foreground transition hover:bg-muted/40"
                    >
                      Go to Parameters
                    </button>
                  </VerticalStack>
                </Surface>
              ) : null}

              {previewLoading ? (
                <VerticalStack gap={3} className="mt-4">
                  <Surface className="h-14 animate-pulse border-border/40 bg-muted/10" />
                  <Surface className="h-24 animate-pulse border-border/40 bg-muted/10" />
                  <Surface className="h-24 animate-pulse border-border/40 bg-muted/10" />
                </VerticalStack>
              ) : null}

              {previewResult && !previewLoading ? (
                <VerticalStack gap={3} className="mt-4">
                  {previewResult.warnings.length > 0 ? (
                    <VerticalStack gap={2}>
                      <p className="text-xs font-black uppercase tracking-widest text-amber-600">
                        Preview Warnings
                      </p>
                      {previewResult.warnings.map((warning, index) => (
                        <Callout key={index} variant="warning">
                          <VerticalStack gap={2}>
                            <DescriptionText className="text-xs">{warning}</DescriptionText>
                            {warning.toLowerCase().includes('access point') ? (
                              <div>
                                <button
                                  type="button"
                                  onClick={handleManageAccessPoints}
                                  className="inline-flex h-8 items-center rounded-md border border-border/60 bg-background px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/40"
                                >
                                  Manage Access Points
                                </button>
                              </div>
                            ) : null}
                          </VerticalStack>
                        </Callout>
                      ))}
                    </VerticalStack>
                  ) : null}
                  {previewResult.diagnostics.length > 0 ? (
                    <VerticalStack gap={2}>
                      <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                        Eligibility Diagnostics
                      </p>
                      {previewResult.diagnostics.map((diagnostic, index) => (
                        <DiagnosticCard key={`${diagnostic.code}-${index}`} diagnostic={diagnostic} />
                      ))}
                    </VerticalStack>
                  ) : null}
                  {previewResult.warnings.length === 0 && previewResult.diagnostics.length === 0 ? (
                    <RegistryEmptyState
                      icon={<Info className="h-6 w-6" />}
                      title="No diagnostics or warnings"
                      description="The preview ran successfully and did not produce any special diagnostic output."
                    />
                  ) : null}
                </VerticalStack>
              ) : null}
            </section>
          ) : null}
        </VerticalStack>
      </WizardWorkspace>
    );
  }, [
    previewOpen,
    previewLoading,
    selectedExperience,
    previewTabs,
    previewTab,
    previewForm,
    previewDependencyWarnings,
    previewError,
    previewResult,
    publicationMap,
    sellingTenantOptions,
    storeOptions,
    agentOptions,
    publicationLookup,
    channelLookup,
    previewSellableById,
    previewBlockedById,
    diagnosticsByProductId,
    handleRunPreview,
    closePreview,
    previewSummaryItems,
    previewSummaryCallout,
  ]);

  const columns = useMemo<ColumnDef<StorefrontExperience>[]>(
    () => [
      {
        id: 'select',
        size: 40,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={value => {
              row.toggleSelected(!!value);
              setSelectedExperienceId(row.original.experience_id);
            }}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: 'storefront',
        accessorFn: experience =>
          `${experience.name} ${experience.experience_key} ${catalogMap.get(experience.catalog_id)?.catalog_key || experience.catalog_key}`,
        size: 320,
        header: 'Storefront',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <button
              type="button"
              onClick={() => openEdit(item)}
              className="group min-w-0 text-left"
              aria-label={`Edit ${item.name}`}
            >
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary">
                  {item.name}
                </p>
                <RegistryStatus label={formatExperienceStatus(item)} variant={readinessVariant(formatExperienceStatus(item))} />
              </div>
              <DescriptionText className="text-[11px]">
                {item.experience_key} • {catalogMap.get(item.catalog_id)?.catalog_key || item.catalog_key}
              </DescriptionText>
            </button>
          );
        },
      },
      {
        accessorKey: 'segment',
        size: 110,
        header: 'Segment',
        cell: ({ row }) => (
          <span className="rounded border border-border/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
            {row.original.segment}
          </span>
        ),
        filterFn: (row, id, value) => value.includes(row.getValue(id)),
      },
      {
        id: 'eligibility_mode',
        accessorKey: 'eligibility_mode',
        size: 160,
        header: 'Eligibility',
        cell: ({ row }) => (
          <span className="text-[11px] font-bold text-foreground/80">
            {row.original.eligibility_mode.replace(/_/g, ' ')}
          </span>
        ),
        filterFn: (row, id, value) => value.includes(row.getValue(id)),
      },
      {
        accessorKey: 'status',
        id: 'status',
        size: 100,
        header: 'Status',
        cell: ({ row }) => {
          const status = formatExperienceStatus(row.original);
          return <RegistryStatus label={status} variant={readinessVariant(status)} />;
        },
        filterFn: (row, id, value) => value.includes(row.getValue(id)),
      },
      {
        id: 'actions',
        enableSorting: false,
        size: 140,
        header: () => <div className="text-right">Actions</div>,
        cell: ({ row }) => {
          const item = row.original;
          const repairAction = firstRepairAction(item);
          return (
            <HorizontalStack gap={2} className="justify-end shrink-0">
              <button
                type="button"
                onClick={() => openPreview(item)}
                aria-label={`Preview ${item.name}`}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void handleValidate(item)}
                aria-label={`Validate ${item.name}`}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => openRules(item)}
                aria-label={`Rules for ${item.name}`}
                disabled={!item.publication_id}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5 disabled:opacity-40"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
              </button>
              {repairingAction === `${item.experience_id}:validate` ? (
                <Activity className="h-7 w-7 animate-spin p-1 text-muted-foreground/50" />
              ) : null}
              {validatingExperienceId === item.experience_id ? (
                <Activity className="h-7 w-7 animate-spin p-1 text-muted-foreground/50" />
              ) : null}
              {repairAction ? (
                <button
                  type="button"
                  onClick={() => void handleRepair(item, repairAction)}
                  aria-label={`Repair ${item.name}`}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
                >
                  <Wrench className="h-3.5 w-3.5" />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => openEdit(item)}
                aria-label={`Edit ${item.name}`}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(item)}
                aria-label={`Archive ${item.name}`}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-rose-400/50 hover:text-rose-600 hover:bg-rose-500/5"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </HorizontalStack>
          );
        },
      },
    ],
    [catalogMap, channelMap, publicationMap, handleDelete, handleValidate, openPreview, openEdit, openRules, repairingAction, validatingExperienceId],
  );
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: STOREFRONT_SORT_FIELDS, registry: 'storefronts' });

  const editorWorkspace = useMemo(() => {
    if (!editorOpen) return null;
    return (
      <StorefrontsWizardEditor
        isOpen={editorOpen}
        mode={editorMode}
        activeTabId={editorActiveTab}
        onActiveTabIdChange={setEditorActiveTab}
        form={form}
        setForm={setForm}
        principalRows={principalRows}
        onAddPrincipal={() => setPrincipalRows(current => [...current, { id: createRowId('principal'), principalType: '', principalId: '' }])}
        onChangePrincipal={(rowId, patch) => setPrincipalRows(current => current.map(row => (row.id === rowId ? { ...row, ...patch } : row)))}
        onRemovePrincipal={rowId => setPrincipalRows(current => current.filter(row => row.id !== rowId))}
        onSave={handleSave}
        saving={saving || bindingsSaving || bindingsLoading}
        validationErrors={validationErrors}
        channelOptions={channelOptions}
        bindingRows={bindingRows}
        bindingPublicationOptions={publicationOptions}
        marketOptions={marketOptions}
        marketGroupOptions={segmentOptions}
        bindingsLoading={bindingsLoading}
        bindingsError={bindingsError}
        onAddBinding={() => {
          setBindingsDirty(true);
          setBindingRows(current => [...current, {
            id: createRowId('publication-route'),
            publicationId: '',
            bindingRole: current.some(row => row.bindingRole === 'primary') ? 'secondary' : 'primary',
            status: 'active',
            priority: 0,
            audience: 'any',
            targetType: 'all',
            targetId: '',
          }]);
        }}
        onChangeBinding={(rowId, patch) => {
          setBindingsDirty(true);
          setBindingRows(current => current.map(row => row.id === rowId ? { ...row, ...patch } : row));
        }}
        onRemoveBinding={rowId => {
          setBindingsDirty(true);
          setBindingRows(current => current.filter(row => row.id !== rowId));
        }}
        readinessIssues={editorMode === 'edit' ? selectedExperience?.readiness?.issues || [] : []}
        readinessStatus={editorMode === 'edit' ? selectedExperience?.readiness?.effective_status || null : null}
        dependencyWarnings={previewDependencyWarnings}
        onManageAccessPoints={handleManageAccessPoints}
        configSnippet={formatSnippet(editorMode === 'edit' ? selectedExperience : null)}
        onClose={() => {
          setEditorOpen(false);
          setEditorActiveTab('identity');
        }}
      />
    );
  }, [
    editorOpen,
    editorMode,
    editorActiveTab,
    form,
    principalRows,
    handleSave,
    saving,
    validationErrors,
    publicationOptions,
    channelOptions,
    marketOptions,
    segmentOptions,
    bindingRows,
    bindingsLoading,
    bindingsSaving,
    bindingsError,
    selectedExperience,
    previewDependencyWarnings,
    handleManageAccessPoints,
  ]);

  const takeoverContent = useMemo(() => {
    if (previewOpen) {
      return previewWorkspace;
    }
    if (rulesWizardOpen && selectedExperience) {
      const policyLookups = {
        product: productOptionLookup,
        variant: variantOptionLookup,
        serviceClass: serviceClassOptionLookup,
        serviceGroup: serviceGroupOptionLookup,
        market: marketOptionLookup,
        segment: segmentOptionLookup,
        tenant: tenantOptionLookup,
        store: storeOptionLookup,
        agent: agentOptionLookup,
      };

      if (ruleEditorOpen) {
        return (
          <EligibilityRuleEditorWizard
            key={editingPolicy?.policy_id ?? 'create'}
            isOpen={true}
            onClose={closeRuleEditor}
            experience={selectedExperience}
            editingPolicy={editingPolicy}
            policyLookups={policyLookups}
            publicationLookup={publicationLookup}
            publicationMap={publicationMap}
            marketOptions={marketOptions}
            segmentOptions={segmentOptions}
            sellingTenantOptions={sellingTenantOptions}
            storeOptions={storeOptions}
            agentOptions={agentOptions}
            productOptions={productOptions}
            variantOptions={variantOptions}
            serviceClassOptions={serviceClassOptions}
            serviceGroupOptions={serviceGroupOptions}
            openPreview={openPreview}
            onSaved={closeRuleEditor}
          />
        );
      }

      return (
        <EligibilityRulesPanel
          experience={selectedExperience}
          policyLookups={policyLookups}
          openPreview={openPreview}
          onBack={closeRules}
          onCreateRule={() => openRuleEditor()}
          onEditRule={openRuleEditor}
        />
      );
    }
    if (editorOpen) {
      return editorWorkspace;
    }
    return null;
  }, [
    previewOpen,
    previewWorkspace,
    rulesWizardOpen,
    ruleEditorOpen,
    editingPolicy,
    editorOpen,
    editorWorkspace,
    closeRules,
    closeRuleEditor,
    openRuleEditor,
    selectedExperience,
    productOptionLookup,
    variantOptionLookup,
    serviceClassOptionLookup,
    serviceGroupOptionLookup,
    marketOptionLookup,
    segmentOptionLookup,
    tenantOptionLookup,
    storeOptionLookup,
    agentOptionLookup,
    publicationLookup,
    publicationMap,
    marketOptions,
    segmentOptions,
    sellingTenantOptions,
    storeOptions,
    agentOptions,
    productOptions,
    variantOptions,
    serviceClassOptions,
    serviceGroupOptions,
    openPreview,
  ]);

  const mainRegistryView = (
    <RegistryWorkspace>
      <RegistryTableSurface>
        <DataTable
            mode="manual"
            columns={columns}
            data={storefronts}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={storefrontColumnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(storefrontColumnFilters) : updater;
              if (next.length === 0 && storefrontColumnFilters.length > 0) {
                query.clear();
                return;
              }
              for (const key of ['segment', 'status']) {
                const filter = next.find(item => item.id === key);
                const values = Array.isArray(filter?.value) ? filter.value.map(String) : [];
                if (values.join('\u0000') !== query.facets[key].join('\u0000')) {
                  query.setFacet(key, values);
                  return;
                }
              }
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalStorefronts}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No storefronts yet</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.segment.length > 0 || query.facets.status.length > 0
                    ? 'No storefronts matched the current search or filters.'
                    : 'Create the first storefront to get started.'}
                </DescriptionText>
                {searchQuery || query.facets.segment.length > 0 || query.facets.status.length > 0 ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search and filters
                  </button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all storefronts..."
                resultCount={totalStorefronts}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <HorizontalStack gap={2}>
                    <DataTableFacetedFilter
                      title="Segment"
                      selectedValues={new Set(query.facets.segment)}
                      onFilterChange={values => query.setFacet('segment', Array.from(values))}
                      counts={storefrontFacetCounts.segment}
                      options={[
                        { label: 'Residential', value: 'residential' },
                        { label: 'Business', value: 'business' },
                      ]}
                    />
                    <DataTableFacetedFilter
                      title="Status"
                      selectedValues={new Set(query.facets.status)}
                      onFilterChange={values => query.setFacet('status', Array.from(values))}
                      counts={storefrontFacetCounts.status}
                      options={[
                        { label: 'Ready', value: 'ready' },
                        { label: 'Draft', value: 'draft' },
                        { label: 'Archived', value: 'archived' },
                      ]}
                    />
                  </HorizontalStack>
                }
              />
            )}
          />
      </RegistryTableSurface>
    </RegistryWorkspace>
  );

  return (
    <div className="flex h-full flex-col">
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        takeoverActive={editorOpen || rulesWizardOpen || previewOpen}
        replaceShellHeaderWhenTakeoverActive
        takeoverContent={takeoverContent}
        registryContent={!editorOpen && !rulesWizardOpen && !previewOpen ? mainRegistryView : null}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[{ label: 'Storefronts' }]}
            title="Storefronts"
            description="Manage public storefront runtimes, publications, and eligibility rules."
          />
        }
      />
    </div>
  );
}
